import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Grid, Typography, Hidden, Link, ButtonGroup, Chip } from '@material-ui/core';
import Truncate from 'react-truncate';

// CONSTANTS
import {
  ROUTE_DISCOVER,
  ROUTE_SHOWS_AND_MOVIES,
  ROUTE_SHOWS_AND_MOVIES_SHOW,
} from '@/constants/routes';
import { contentStatuses, DisplayType } from '@/constants/watchList';
import {
  LOCATIONS_CONTENT_MODAL,
  LOCATIONS_MODAL,
  LOCATIONS_SHOW_DETAILS_PAGE,
} from '@/constants/locations';
import { actions, triggered } from '@/constants/analytics';

// UTILITY
import { getContentYear, getPlaceholderUrl } from '@/utility/content';
import { getRuntime } from '@/utility/common';

// STORE
import { AppActions } from '@/store/actions';

// HOC
import withMediaQuery from '@/hoc/withMediaQuery';
import Guard from '@/hoc/Guard';
import Measure from '@/hoc/Measure';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import Loader from '@/components/Loader';
import DetailsButton from '@/components/Cards/ShowMovieCard/help/DetailsButton';
import RouterBreadcrumbs from '@/components/RouterBreadcrumbs';
import AddRemoveButton from '@/components/Buttons/AddRemoveButton';
import RouterLink from '@/components/Link';
import ShowMoreText from '@/components/ShowMoreText';
import Scores from '@/components/ShowMovies/Scores';
import Genres from '@/components/ShowMovies/Genres';
import InfoList from '@/components/ShowMovies/InfoList';
import WhereToWatch from '@/components/ShowMovies/WhereToWatch';
import ShowMovieVotes from '@/components/ShowMovies/ShowMovieVotes';
import DialogReport from '@/components/Dialogs/DialogReport';

// STYLES
import { Container } from '../../styles';
import { Wrapper } from './styles';

const rentAppsKey = 'apps-rent-purchase';

function getSeasonsData(t, data, curSeason) {
  const seasonsCount = data.seasons.length;

  const seasons = [
    ...(seasonsCount > 1
      ? [
          {
            season_number: 'all',
            name: t('showDetails.seasonsAvailability'),
            apps: data.apps,
            [rentAppsKey]: data[rentAppsKey],
          },
        ]
      : []),
    ...data.seasons,
  ];

  const curSeasonData = seasons.find(season => season.season_number === curSeason);

  if (!curSeasonData) {
    return [[], seasons, seasonsCount];
  }

  const apps = curSeasonData.apps || [];
  const rentApps = curSeasonData[rentAppsKey] || [];
  const seasonApps = [...apps, ...rentApps];

  return [seasonApps, seasons, seasonsCount];
}

class Hero extends PureComponent {
  state = {
    currentSeason: 1,
    reportIsOpen: false,
  };

  componentDidMount() {
    const { streamingData } = this.props;

    if (streamingData) {
      const seasonsCount = streamingData.seasons?.length;

      if (seasonsCount) {
        this.setCurrSeason();
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { streamingData } = this.props;

    if (streamingData) {
      const seasonsCount = streamingData.seasons?.length;
      const prevSeasonsCount = prevProps.streamingData?.seasons?.length;

      if (seasonsCount && seasonsCount !== prevSeasonsCount) {
        this.setCurrSeason();
      }
    }
  }

  getCurrentSeason = streamingData => {
    const { channels, apps, seasons } = streamingData;
    const seasonsCount = seasons?.length;
    const season_number = seasons[0]?.season_number;
    const hasSeasonsApps = apps?.length || streamingData[rentAppsKey]?.length;
    const hasChannels = channels?.length;

    if (hasChannels && !hasSeasonsApps) {
      return `channel${channels[0].id}`;
    }

    return seasonsCount > 1 ? 'all' : Number.isInteger(season_number) ? season_number : null;
  };

  setCurrSeason = () => {
    const { streamingData } = this.props;

    if (streamingData) {
      const currentSeason = this.getCurrentSeason(streamingData);

      if (currentSeason !== null) {
        this.setState({ currentSeason });
      }
    }
  };

  getYearRange = (range, status = '') => {
    const { t } = this.props;
    const startYear = range.start ? getContentYear(range.start) : '';
    const endYear = range.end ? getContentYear(range.end) : '';
    let yearRange = startYear;

    if (!endYear && (status === 'In Production' || status === 'Returning Series')) {
      yearRange += ` - ${t('common.movie.current')}`;
    } else if (endYear > startYear) {
      yearRange += ` - ${endYear}`;
    }

    return yearRange;
  };

  handleReportDialogState = state => {
    this.setState({ reportIsOpen: state });
  };

  render() {
    const { currentSeason, reportIsOpen } = this.state;
    const {
      analyticsLocation,
      t,
      router: { routerMatch },
      data,
      partnerData,
      loadingAction,
      addToWatchList,
      removeFromWatchList,
      changeContentStatus,
      matches,
      isAuth,
      displayType,
      streamingData,
      streamingDataLoading,
      createFeedback,
      onNewEpisodesClick,
    } = this.props;
    const isPage = displayType === DisplayType.PAGE;
    const isDialog = displayType === DisplayType.DIALOG;
    const hasPartner = !!Object.keys(partnerData).length;
    const inWatchlist = data?.show.in_watchlist;
    const content_status = data?.show.user_content_status;
    const isWatched = content_status === contentStatuses.watched;
    const isNotInterested = content_status === contentStatuses.notInterested;
    const isCurrentlyWatching = content_status === contentStatuses.currentlyWatching;
    const exclude = ['show'];
    const votes = data?.show.votes;
    const hasVotes = !!votes;
    const hasNewEpisodes = !!data?.show?.current_episodes_count;

    const isShowMoviesShowRoute = routerMatch({ path: ROUTE_SHOWS_AND_MOVIES_SHOW });
    let use_marketplace = true;

    if (hasPartner) {
      use_marketplace = !!partnerData.settings?.use_marketplace;
    }

    const hasSeasons = streamingData?.seasons?.length;

    const contentInfo = [
      { title: t('common.seasons'), value: data.seasons_count || streamingData?.seasons?.length },
      { title: t('common.movie.status'), value: data.show?.status || '' },
      { title: t('common.movie.runtime'), value: getRuntime(data.show?.duration || '') },
      // { title: t('common.movie.imdb'), value: data.show.rating_imdb },
    ];

    const [selectedApps, watchOptions] = hasSeasons
      ? getSeasonsData(t, streamingData, currentSeason)
      : [[], [], 0];

    return (
      <Wrapper className={clsx('section-wrapper hero-section', `hero-section--${displayType}`)}>
        <div className="page-content">
          {isPage && use_marketplace && (
            <>
              <Hidden xsDown>
                <RouterBreadcrumbs
                  className="breadcrumbs breadcrumbs--dark hero-section__breadcrumbs"
                  lastBreadcrumbText={data.show.content_title}
                  exclude={exclude}
                />
              </Hidden>
              <Hidden smUp>
                <Link
                  className="p5 hero-section__back-link"
                  color="textSecondary"
                  component={RouterLink}
                  to={isShowMoviesShowRoute ? ROUTE_SHOWS_AND_MOVIES : ROUTE_DISCOVER}
                  location={LOCATIONS_SHOW_DETAILS_PAGE}
                >
                  <i className="icon-chevron-left" />
                  {t(`common.menu.${isShowMoviesShowRoute ? 'tvShowsAndMovies' : 'explore'}`)}
                </Link>
              </Hidden>
            </>
          )}
          <Container className="main-content">
            <div className="hero-section__content">
              <Grid container spacing={4}>
                <Grid item xs={12} md={isPage ? 5 : 4}>
                  <div className="hero-section__left-section">
                    <div
                      className={clsx(
                        'hero-section__content-image-wrapper',
                        hasVotes && 'content__votes',
                      )}
                    >
                      <img
                        className="hero-section__content-image"
                        src={
                          data.show.thumbnail ||
                          getPlaceholderUrl({
                            title: data.show.content_title,
                            width: 355,
                            height: 530,
                          })
                        }
                        alt="show-poster"
                      />
                      {hasVotes && <ShowMovieVotes data={votes} />}
                      {hasNewEpisodes && (
                        <Chip
                          className="hero-section__chip"
                          onClick={onNewEpisodesClick}
                          label={t(
                            `common.${
                              data.show.current_episodes_count === 1
                                ? 'newEpisodeAvailable'
                                : 'newEpisodesAvailable'
                            }`,
                          )}
                          color="secondary"
                        />
                      )}
                    </div>
                    <Guard
                      iframe={false}
                      partner={{
                        search_movies_shows: true,
                        use_authorizing: true,
                      }}
                    >
                      <AddRemoveButton
                        className="hero-section__content-button sizeExtraLarge"
                        variant="outlined"
                        color="primary"
                        disabled={loadingAction}
                        onClick={() => addToWatchList(data.show)}
                        handleDelete={() => removeFromWatchList(data.show)}
                        added={inWatchlist}
                        minWidth={171}
                        removeText={t('buttons.removeFromWatchlist')}
                        checkAuth
                      >
                        {t(`buttons.${inWatchlist ? 'addedToWatchlist' : 'addToWatchlist'}`)}
                      </AddRemoveButton>
                      <ButtonGroup
                        className="hero-section__content-buttons type-secondary"
                        variant="contained"
                      >
                        <AddRemoveButton
                          className={clsx('sizeExtraLarge', !isWatched && 'grey')}
                          disabled={loadingAction}
                          onClick={() => changeContentStatus(data.show.id, contentStatuses.watched)}
                          handleDelete={() => changeContentStatus(data.show.id, null)}
                          added={isWatched}
                          minWidth={80}
                          checkAuth
                          status={contentStatuses.watched}
                        >
                          {t('buttons.watched')}
                        </AddRemoveButton>
                        <AddRemoveButton
                          className={clsx('sizeExtraLarge', !isCurrentlyWatching && 'grey')}
                          disabled={loadingAction}
                          onClick={() =>
                            changeContentStatus(data.show.id, contentStatuses.currentlyWatching)
                          }
                          handleDelete={() => changeContentStatus(data.show.id, null)}
                          added={isCurrentlyWatching}
                          minWidth={126}
                          checkAuth
                          status={contentStatuses.currentlyWatching}
                          checkAuthProps={{
                            authComponentProps: {
                              analyticsData: {
                                triggered: triggered.show,
                                action: actions.changeContentStatus,
                                content_id: data.show.id,
                                partner_id: partnerData.id || 0,
                                location: analyticsLocation,
                              },
                            },
                          }}
                        >
                          {t('buttons.watchingNow')}
                        </AddRemoveButton>
                        <AddRemoveButton
                          className={clsx('sizeExtraLarge', !isNotInterested && 'grey')}
                          disabled={loadingAction}
                          onClick={() =>
                            changeContentStatus(data.show.id, contentStatuses.notInterested)
                          }
                          handleDelete={() => changeContentStatus(data.show.id, null)}
                          added={isNotInterested}
                          minWidth={125}
                          checkAuth
                          status={contentStatuses.notInterested}
                        >
                          {t('buttons.notInterested')}
                        </AddRemoveButton>
                      </ButtonGroup>
                      {isDialog && (
                        <DetailsButton
                          location={LOCATIONS_CONTENT_MODAL}
                          data={data.show}
                          hasStreamingData={!streamingDataLoading && !!selectedApps?.length}
                          className="sizeExtraLarge hero-section__content-buttons"
                          variant="contained"
                          color="primary"
                          fullWidth
                        >
                          {t('buttons.seeDetails')}
                        </DetailsButton>
                      )}
                    </Guard>
                  </div>
                </Grid>
                <Grid item xs={12} md={isPage ? 7 : 8}>
                  <Typography className="title m-b-15" variant={matches || isDialog ? 'h3' : 'h2'}>
                    {data.show.content_title}&nbsp;
                    {(data.show.year_range?.start || data.show.year_range?.end) && (
                      <Typography
                        className={isPage ? 'p1' : 'p4'}
                        component="span"
                        color="textSecondary"
                      >
                        ({this.getYearRange(data.show.year_range, data.show.status)})
                      </Typography>
                    )}
                  </Typography>

                  <Scores
                    imdb={data.show?.imdb_rating || ''}
                    rTomatoes={data.show?.rotten_tomatoes?.toString() || ''}
                    mbRating={data.show?.average_user_rating?.toString() || ''}
                  />

                  <Genres data={data.show.genres || []} />

                  <InfoList className="m-b-15" data={contentInfo} />

                  {data.show.overview &&
                    (isDialog ? (
                      <Measure>
                        {([ref, { width }]) => (
                          <Typography
                            ref={ref}
                            className={clsx('hero-section__content-description', isDialog && 'p4')}
                          >
                            <Truncate lines={4} trimWhitespace width={width}>
                              {data.show.overview}
                            </Truncate>
                          </Typography>
                        )}
                      </Measure>
                    ) : (
                      <ShowMoreText openOnce>
                        <Typography
                          className={clsx('hero-section__content-description', isDialog && 'p4')}
                        >
                          {data.show.overview}
                        </Typography>
                      </ShowMoreText>
                    ))}

                  {streamingDataLoading ? (
                    <div className="m-b-30 m-t-30">
                      <Loader active position="relative" />
                    </div>
                  ) : (
                    <WhereToWatch
                      type="show"
                      data={selectedApps}
                      content={{ ...data.show, watch_list_status: data.watch_list_status }}
                      className={clsx(
                        'where-to-watch__section',
                        !selectedApps.length && 'where-to-watch__no-results',
                      )}
                      seasons={watchOptions}
                      curSeason={currentSeason}
                      subscribed={isAuth && !!data?.show.subscription_status}
                      changeContentStatus={changeContentStatus}
                      analyticsLocation={isDialog ? LOCATIONS_MODAL : ''}
                    />
                  )}
                  {!streamingDataLoading && (
                    <Link
                      className="hero-section__report-link"
                      component="button"
                      color="inherit"
                      onClick={() => this.handleReportDialogState(true)}
                    >
                      {t('buttons.letUsKnow')}
                    </Link>
                  )}
                </Grid>
              </Grid>
            </div>
          </Container>
        </div>
        <DialogReport
          open={reportIsOpen}
          data={{
            type: 'content',
            contentType: 'show',
            entity_id: data.show?.id,
            content_title: data.show.content_title,
          }}
          onClose={() => this.handleReportDialogState(false)}
          onSubmit={createFeedback}
          analyticsLocation={analyticsLocation}
        />
      </Wrapper>
    );
  }
}

Hero.defaultProps = {
  streamingDataLoading: false,
  displayType: DisplayType.PAGE,
  loadingAction: false,
  onNewEpisodesClick: () => {},
};

Hero.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  streamingData: PropTypes.object,
  partnerData: PropTypes.object.isRequired,
  loadingAction: PropTypes.bool,
  addToWatchList: PropTypes.func.isRequired,
  removeFromWatchList: PropTypes.func.isRequired,
  changeContentStatus: PropTypes.func.isRequired,
  matches: PropTypes.bool.isRequired,
  isAuth: PropTypes.bool.isRequired,
  displayType: PropTypes.string,
  streamingDataLoading: PropTypes.bool,
  analyticsLocation: PropTypes.string,
  createFeedback: PropTypes.func.isRequired,
  onNewEpisodesClick: PropTypes.func,
};

const mapDispatchToProps = dispatch => {
  return {
    createFeedback: (data, callback) => {
      dispatch(AppActions.createFeedback.action({ data, callback }));
    },
  };
};

export default compose(
  connect(null, mapDispatchToProps),
  withTranslation(),
  withMediaQuery({ matches: theme => theme.breakpoints.down('xs') }),
  withRouter,
)(Hero);
